import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Progress } from './ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Users,
  TrendingUp,
  TrendingDown,
  UserCheck,
  UserMinus,
  Clock,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Loader2,
  BarChart3,
  PieChart,
  Crown,
  Info,
  Download,
  Brain,
  Filter,
  Building2,
  MapPin,
  X
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { usePlan } from '../contexts/PlanContext';

// Composant pour une carte KPI
function KPICard({ title, value, unit = '', icon: Icon, trend, trendValue, color = 'blue', loading, locked }) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    orange: 'bg-orange-100 text-orange-600',
    red: 'bg-red-100 text-red-600',
    purple: 'bg-purple-100 text-purple-600',
    slate: 'bg-slate-100 text-slate-600'
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (locked) {
    return (
      <Card className="opacity-60">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className={`p-3 ${colorClasses[color]} rounded-lg`}>
              <Icon className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-slate-500">{title}</p>
              <div className="flex items-center gap-2">
                <Crown className="w-4 h-4 text-amber-500" />
                <span className="text-sm text-slate-400">PRO requis</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-4">
          <div className={`p-3 ${colorClasses[color]} rounded-lg`}>
            <Icon className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-slate-500">{title}</p>
            <p className="text-2xl font-bold text-slate-900">
              {value !== null && value !== undefined ? value : '-'}{unit}
            </p>
            {trend && (
              <div className={`flex items-center gap-1 text-sm ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                {trend === 'up' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                <span>{trendValue}</span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Composant pour le graphique de répartition (simple)
function DistributionChart({ data, title, colorScheme = 'default' }) {
  const colors = {
    default: ['bg-blue-500', 'bg-green-500', 'bg-orange-500', 'bg-purple-500', 'bg-red-500', 'bg-slate-500'],
    gender: ['bg-blue-500', 'bg-pink-500', 'bg-slate-400'],
    contract: ['bg-green-500', 'bg-yellow-500', 'bg-orange-500', 'bg-red-500', 'bg-purple-500', 'bg-slate-400']
  };
  
  const total = data.reduce((sum, item) => sum + item.value, 0);
  
  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-slate-700">{title}</h4>
      <div className="space-y-2">
        {data.map((item, index) => {
          const percentage = total > 0 ? (item.value / total) * 100 : 0;
          return (
            <div key={item.label} className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">{item.label}</span>
                <span className="font-medium">{item.value} ({percentage.toFixed(1)}%)</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${colors[colorScheme][index % colors[colorScheme].length]} rounded-full transition-all`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function KPIDashboard() {
  const { isPro, isFree } = usePlan();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [kpis, setKpis] = useState(null);
  const [history, setHistory] = useState([]);
  const [trends, setTrends] = useState(null);
  const [insights, setInsights] = useState(null);
  const [creatingSnapshot, setCreatingSnapshot] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  
  // Filtres
  const [filters, setFilters] = useState({
    department: 'all',
    site: 'all'
  });
  const [filterOptions, setFilterOptions] = useState({
    departments: [],
    sites: []
  });
  const [loadingFilters, setLoadingFilters] = useState(true);

  // Fetch filter options
  const fetchFilterOptions = useCallback(async () => {
    try {
      setLoadingFilters(true);
      const response = await api.get('/kpi/filters');
      setFilterOptions({
        departments: response.data.departments || [],
        sites: response.data.sites || []
      });
    } catch (error) {
      console.error('Error fetching filter options:', error);
    } finally {
      setLoadingFilters(false);
    }
  }, []);

  const fetchKPIs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.department && filters.department !== 'all') {
        params.append('department', filters.department);
      }
      if (filters.site && filters.site !== 'all') {
        params.append('site', filters.site);
      }
      const queryString = params.toString();
      const response = await api.get(`/kpi/current${queryString ? `?${queryString}` : ''}`);
      setKpis(response.data);
    } catch (error) {
      console.error('Error fetching KPIs:', error);
      if (error.response?.status !== 403) {
        toast.error('Erreur lors du chargement des indicateurs');
      }
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchHistory = useCallback(async () => {
    if (!isPro) return;
    try {
      const response = await api.get('/kpi/history?months=12');
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  }, [isPro]);

  const fetchTrends = useCallback(async () => {
    if (!isPro) return;
    try {
      const response = await api.get('/kpi/trends');
      setTrends(response.data);
    } catch (error) {
      console.error('Error fetching trends:', error);
    }
  }, [isPro]);

  const fetchInsights = useCallback(async () => {
    if (!isPro) return;
    try {
      const response = await api.get('/kpi/ai-insights');
      setInsights(response.data);
    } catch (error) {
      console.error('Error fetching insights:', error);
    }
  }, [isPro]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchKPIs();
    if (isPro) {
      fetchHistory();
      fetchTrends();
      fetchInsights();
    }
  }, [fetchKPIs, fetchHistory, fetchTrends, fetchInsights, isPro]);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      department: 'all',
      site: 'all'
    });
  };

  // Check if any filter is active
  const hasActiveFilters = filters.department !== 'all' || filters.site !== 'all';

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      fetchKPIs(),
      isPro ? fetchHistory() : Promise.resolve(),
      isPro ? fetchTrends() : Promise.resolve(),
      isPro ? fetchInsights() : Promise.resolve()
    ]);
    setRefreshing(false);
    toast.success('Indicateurs actualisés');
  };

  const handleCreateSnapshot = async () => {
    setCreatingSnapshot(true);
    try {
      const response = await api.post('/kpi/snapshot');
      toast.success(response.data.message);
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du snapshot');
    } finally {
      setCreatingSnapshot(false);
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      // Construire les paramètres avec les filtres actifs
      const params = new URLSearchParams();
      if (filters.department && filters.department !== 'all') {
        params.append('department', filters.department);
      }
      if (filters.site && filters.site !== 'all') {
        params.append('site', filters.site);
      }
      const queryString = params.toString();
      
      const response = await api.get(`/kpi/export-pdf${queryString ? `?${queryString}` : ''}`, {
        responseType: 'blob'
      });
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Ajouter les filtres au nom du fichier si actifs
      let fileName = `Rapport_KPI_RH_${new Date().toISOString().split('T')[0]}`;
      if (filters.department !== 'all') {
        const deptName = filterOptions.departments.find(d => d.id === filters.department)?.name;
        if (deptName) fileName += `_${deptName.replace(/[^a-zA-Z0-9]/g, '_')}`;
      }
      fileName += '.pdf';
      
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Rapport PDF téléchargé' + (hasActiveFilters ? ' (filtré)' : ''));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération du PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleGenerateAIAnalysis = async () => {
    setGeneratingAI(true);
    try {
      const response = await api.get('/kpi/ai-insights?use_ai=true');
      setInsights(response.data);
      
      // Afficher les informations sur les crédits
      if (response.data.credits_consumed > 0) {
        toast.success(
          `Analyse IA générée (${response.data.credits_consumed} crédits utilisés, ${response.data.credits_remaining} restants)`,
          { duration: 4000 }
        );
      } else if (response.data.ai_powered) {
        toast.success('Analyse IA générée');
      } else {
        toast.info('Analyse basique générée (sans IA)');
      }
    } catch (error) {
      // Gérer l'erreur 402 (crédits insuffisants)
      if (error.response?.status === 402) {
        const detail = error.response?.data?.detail;
        toast.error(
          <div>
            <p className="font-semibold">Crédits IA insuffisants</p>
            <p className="text-sm">Vous avez {detail?.credits_available || 0} crédits, il en faut {detail?.credits_required || 5}.</p>
            <a href="/billing/credits" className="text-blue-400 underline text-sm">Acheter des crédits →</a>
          </div>,
          { duration: 8000 }
        );
      } else {
        toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || 'Erreur lors de la génération');
      }
    } finally {
      setGeneratingAI(false);
    }
  };

  // Préparer les données pour les graphiques
  const genderData = kpis ? [
    { label: 'Hommes', value: kpis.effectifHommes || 0 },
    { label: 'Femmes', value: kpis.effectifFemmes || 0 }
  ].filter(d => d.value > 0) : [];

  const contractData = kpis ? [
    { label: 'CDI', value: kpis.effectifCDI || 0 },
    { label: 'CDD', value: kpis.effectifCDD || 0 },
    { label: 'Intérim', value: kpis.effectifInterim || 0 },
    { label: 'Prestation', value: kpis.effectifPrestation || 0 },
    { label: 'Stage', value: kpis.effectifStage || 0 },
    { label: 'Autre', value: kpis.effectifAutre || 0 }
  ].filter(d => d.value > 0) : [];

  const categoryData = kpis ? [
    { label: 'Cadres', value: kpis.effectifCadres || 0 },
    { label: 'Agents de maîtrise', value: kpis.effectifAgentsMaitrise || 0 },
    { label: 'Employés', value: kpis.effectifEmployes || 0 },
    { label: 'Ouvriers', value: kpis.effectifOuvriers || 0 }
  ].filter(d => d.value > 0) : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Indicateurs RH</h2>
          <p className="text-slate-600">Tableau de bord de pilotage des ressources humaines</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {isPro && (
            <>
              <Button 
                variant="outline" 
                onClick={handleExportPdf} 
                disabled={exportingPdf}
                className="text-blue-600 border-blue-200 hover:bg-blue-50"
              >
                {exportingPdf ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Export PDF
              </Button>
              <Button variant="outline" onClick={handleCreateSnapshot} disabled={creatingSnapshot}>
                {creatingSnapshot ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Calendar className="w-4 h-4 mr-2" />
                )}
                Créer un snapshot
              </Button>
            </>
          )}
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Filters Section */}
      <Card className="border-slate-200">
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-2 text-slate-700">
              <Filter className="w-4 h-4" />
              <span className="font-medium text-sm">Filtres :</span>
            </div>
            
            <div className="flex flex-wrap items-center gap-3 flex-1">
              {/* Department Filter */}
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-slate-400" />
                <Select 
                  value={filters.department} 
                  onValueChange={(value) => handleFilterChange('department', value)}
                  disabled={loadingFilters}
                >
                  <SelectTrigger className="w-[200px] h-9" data-testid="filter-department">
                    <SelectValue placeholder="Département" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les départements</SelectItem>
                    {filterOptions.departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Site Filter */}
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-slate-400" />
                <Select 
                  value={filters.site} 
                  onValueChange={(value) => handleFilterChange('site', value)}
                  disabled={loadingFilters}
                >
                  <SelectTrigger className="w-[200px] h-9" data-testid="filter-site">
                    <SelectValue placeholder="Site" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les sites</SelectItem>
                    {filterOptions.sites.map((site) => (
                      <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Reset Filters Button */}
              {hasActiveFilters && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={resetFilters}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <X className="w-4 h-4 mr-1" />
                  Réinitialiser
                </Button>
              )}
            </div>
            
            {/* Active Filters Indicator */}
            {hasActiveFilters && (
              <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                Filtres actifs
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Plan STARTER limitation message */}
      {isFree && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Crown className="w-5 h-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">Version limitée</p>
                <p className="text-sm text-amber-700">
                  Passez au plan PRO pour accéder à lhistorique, aux tendances et aux analyses IA.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards - Row 1: Effectifs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Effectif total"
          value={kpis?.effectifTotal}
          icon={Users}
          color="blue"
          loading={loading}
        />
        <KPICard
          title="Effectif actif"
          value={kpis?.effectifActif}
          icon={UserCheck}
          color="green"
          loading={loading}
        />
        <KPICard
          title="Sorties"
          value={kpis?.effectifSorti}
          icon={UserMinus}
          color="red"
          loading={loading}
        />
        <KPICard
          title="Entrées ce mois"
          value={kpis?.entreesMois}
          icon={TrendingUp}
          color="purple"
          loading={loading}
        />
      </div>

      {/* KPI Cards - Row 2: Taux */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Taux CDI"
          value={kpis?.tauxCDI}
          unit="%"
          icon={CheckCircle2}
          color="green"
          loading={loading}
        />
        <KPICard
          title="Taux encadrement"
          value={kpis?.tauxEncadrement}
          unit="%"
          icon={Users}
          color="purple"
          loading={loading}
        />
        <KPICard
          title="Taux turnover (annualisé)"
          value={kpis?.tauxTurnover}
          unit="%"
          icon={TrendingDown}
          color="orange"
          loading={loading}
          locked={isFree}
        />
        <KPICard
          title="Taux démission (annualisé)"
          value={kpis?.tauxDemission}
          unit="%"
          icon={UserMinus}
          color="red"
          loading={loading}
          locked={isFree}
        />
      </div>

      {/* KPI Cards - Row 3: Moyennes */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Âge moyen"
          value={kpis?.ageMoyen}
          unit=" ans"
          icon={Calendar}
          color="slate"
          loading={loading}
          locked={isFree}
        />
        <KPICard
          title="Ancienneté moyenne"
          value={kpis?.ancienneteMoyenne}
          unit=" ans"
          icon={Clock}
          color="blue"
          loading={loading}
          locked={isFree}
        />
        <KPICard
          title="Ratio H/F"
          value={kpis?.ratioHommesFemmes}
          icon={Users}
          color="purple"
          loading={loading}
        />
        <KPICard
          title="Taux embauche (annualisé)"
          value={kpis?.tauxEmbauche}
          unit="%"
          icon={TrendingUp}
          color="green"
          loading={loading}
          locked={isFree}
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <PieChart className="w-5 h-5" />
              Répartition H/F
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <DistributionChart data={genderData} title="" colorScheme="gender" />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <BarChart3 className="w-5 h-5" />
              Par type de contrat
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <DistributionChart data={contractData} title="" colorScheme="contract" />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="w-5 h-5" />
              Par catégorie
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-40 w-full" />
            ) : categoryData.length > 0 ? (
              <DistributionChart data={categoryData} title="" colorScheme="default" />
            ) : (
              <p className="text-sm text-slate-500 text-center py-8">
                Aucune catégorie renseignée
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Data Quality Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="w-5 h-5" />
            Qualité des données
          </CardTitle>
          <CardDescription>
            Complétude des informations RH pour des indicateurs fiables
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Sexe renseigné</span>
                <span className="font-medium">{kpis?.tauxRenseignementSexe || 0}%</span>
              </div>
              <Progress value={kpis?.tauxRenseignementSexe || 0} className="h-2" />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Date naissance renseignée</span>
                <span className="font-medium">{kpis?.tauxRenseignementAge || 0}%</span>
              </div>
              <Progress value={kpis?.tauxRenseignementAge || 0} className="h-2" />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Catégorie renseignée</span>
                <span className="font-medium">{kpis?.tauxRenseignementCategorie || 0}%</span>
              </div>
              <Progress value={kpis?.tauxRenseignementCategorie || 0} className="h-2" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI Insights Section (PRO only) */}
      {isPro && (
        <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-blue-50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-purple-900">
                  <Brain className="w-5 h-5" />
                  Analyse IA
                  <Badge className="bg-purple-100 text-purple-700 ml-2">BUSINESS</Badge>
                  {insights?.ai_powered && (
                    <Badge className="bg-green-100 text-green-700 ml-1">GPT-5.1</Badge>
                  )}
                </CardTitle>
                <CardDescription className="text-purple-700">
                  Insights et recommandations basés sur vos données RH
                </CardDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleGenerateAIAnalysis}
                disabled={generatingAI}
                className="border-purple-300 text-purple-700 hover:bg-purple-100"
              >
                {generatingAI ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Brain className="w-4 h-4 mr-2" />
                )}
                {insights?.ai_powered ? 'Régénérer' : 'Générer analyse IA'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Health Score */}
            {insights?.health_score !== undefined && (
              <div className="flex items-center gap-6 p-4 bg-white rounded-xl border border-purple-100">
                <div className="relative">
                  <svg className="w-24 h-24 transform -rotate-90">
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="#e2e8f0"
                      strokeWidth="8"
                      fill="none"
                    />
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke={insights.health_score >= 70 ? '#22c55e' : insights.health_score >= 50 ? '#f59e0b' : '#ef4444'}
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray={`${(insights.health_score / 100) * 251.2} 251.2`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`text-2xl font-bold ${
                      insights.health_score >= 70 ? 'text-green-600' : 
                      insights.health_score >= 50 ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {insights.health_score}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-lg font-semibold text-slate-900">{insights.health_label || 'Score de santé RH'}</p>
                  {insights.summary && (
                    <p className="text-sm text-slate-600 mt-1">{insights.summary}</p>
                  )}
                </div>
              </div>
            )}

            {/* Key Actions */}
            {insights?.key_actions?.length > 0 && (
              <div className="p-4 bg-white rounded-xl border border-blue-100">
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-blue-600" />
                  Actions prioritaires
                </h4>
                <ul className="space-y-2">
                  {insights.key_actions.slice(0, 3).map((action, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-slate-700">
                      <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {index + 1}
                      </span>
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Insights List */}
            {insights?.insights?.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-semibold text-slate-900">Analyse détaillée</h4>
                {insights.insights.map((insight, index) => (
                  <div 
                    key={index}
                    className={`p-4 rounded-lg border ${
                      insight.type === 'success' ? 'bg-green-50 border-green-200' :
                      insight.type === 'warning' ? 'bg-amber-50 border-amber-200' :
                      insight.type === 'danger' ? 'bg-red-50 border-red-200' :
                      'bg-blue-50 border-blue-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {insight.type === 'success' ? (
                        <CheckCircle2 className="w-5 h-5 mt-0.5 text-green-600" />
                      ) : (
                        <AlertTriangle className={`w-5 h-5 mt-0.5 ${
                          insight.type === 'warning' ? 'text-amber-600' :
                          insight.type === 'danger' ? 'text-red-600' :
                          'text-blue-600'
                        }`} />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">{insight.title}</p>
                          {insight.category && (
                            <Badge variant="outline" className="text-xs">{insight.category}</Badge>
                          )}
                          {insight.priority === 'high' && (
                            <Badge className="bg-red-100 text-red-700 text-xs">Priorité haute</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mt-1">{insight.message}</p>
                        {insight.recommendation && (
                          <p className="text-sm text-slate-700 mt-2 p-2 bg-white/70 rounded flex items-start gap-2">
                            <span>💡</span>
                            <span>{insight.recommendation}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Empty state */}
            {(!insights || !insights.insights?.length) && !generatingAI && (
              <div className="text-center py-8">
                <Brain className="w-12 h-12 text-purple-300 mx-auto mb-3" />
                <p className="text-slate-600">Cliquez sur "Générer analyse IA" pour obtenir des insights personnalisés</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Trends Alert Section (PRO only) */}
      {isPro && trends?.alerts?.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-900">
              <AlertTriangle className="w-5 h-5" />
              Alertes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {trends.alerts.map((alert, index) => (
                <div key={index} className="flex items-center gap-2 text-orange-800">
                  <AlertTriangle className="w-4 h-4" />
                  <span>{alert.message}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* History Section (PRO only) */}
      {isPro && history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Historique des snapshots
              <Badge variant="outline" className="ml-2">{history.length} mois</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3">Période</th>
                    <th className="text-right py-2 px-3">Effectif</th>
                    <th className="text-right py-2 px-3">CDI</th>
                    <th className="text-right py-2 px-3">Turnover</th>
                    <th className="text-right py-2 px-3">H/F</th>
                  </tr>
                </thead>
                <tbody>
                  {history.slice(0, 6).map((snap) => (
                    <tr key={snap.period} className="border-b hover:bg-slate-50">
                      <td className="py-2 px-3 font-medium">{snap.period}</td>
                      <td className="text-right py-2 px-3">{snap.effectifActif}</td>
                      <td className="text-right py-2 px-3">{snap.tauxCDI}%</td>
                      <td className="text-right py-2 px-3">{snap.tauxTurnover}%</td>
                      <td className="text-right py-2 px-3">{snap.ratioHommesFemmes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* FREE Plan Upgrade Banner */}
      {isFree && (
        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-900">
              <Crown className="w-5 h-5 text-amber-600" />
              Passez au Plan BUSINESS
            </CardTitle>
            <CardDescription className="text-amber-700">
              Débloquez tout le potentiel de vos indicateurs RH
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-amber-100">
                <Brain className="w-6 h-6 text-purple-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-slate-900">Analyse IA</p>
                  <p className="text-sm text-slate-600">
                    GPT-5.1 analyse vos KPI et génère des recommandations personnalisées
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-amber-100">
                <Download className="w-6 h-6 text-blue-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-slate-900">Export PDF Direction</p>
                  <p className="text-sm text-slate-600">
                    Générez des rapports professionnels pour vos comités de direction
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-amber-100">
                <Calendar className="w-6 h-6 text-green-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-slate-900">Historique & Tendances</p>
                  <p className="text-sm text-slate-600">
                    Suivez l'évolution de vos KPI sur plusieurs mois et détectez les tendances
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-amber-100">
                <AlertTriangle className="w-6 h-6 text-orange-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-slate-900">Alertes intelligentes</p>
                  <p className="text-sm text-slate-600">
                    Soyez alerté automatiquement des variations importantes de vos indicateurs
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between pt-4 border-t border-amber-200">
              <div>
                <p className="text-sm text-amber-800">
                  <span className="font-semibold">Plan STARTER actuel</span> - KPI de base uniquement
                </p>
              </div>
              <Button 
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                onClick={() => window.location.href = '/pricing'}
              >
                <Crown className="w-4 h-4 mr-2" />
                Passer au PRO
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
